import { Routes } from '@angular/router';

import { authnGuard } from './core/auth/authn.guard';
import { accessGuard } from './core/guards/access.guard';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { ShellComponent } from './layout/shell.component';

/**
 * Top-level routes. Feature screens are lazy-loaded so the initial bundle
 * stays small as more screens are ported from the prototype.
 */
export const APP_ROUTES: Routes = [
  {
    // Authenticated but not authorized for DROS (missing the required role).
    // Outside the shell so it stays reachable without passing the access guards.
    path: 'access-denied',
    title: 'Access not granted · DROS',
    loadComponent: () =>
      import('./features/access-denied/access-denied.component').then(
        (m) => m.AccessDeniedComponent,
      ),
  },
  {
    path: '',
    component: ShellComponent,
    // authnGuard = real Keycloak auth + required-role authorization (this app).
    // accessGuard = in-app role→route authorization from the Users & Access matrix.
    canActivateChild: [authnGuard, accessGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'today' },
      {
        path: 'today',
        title: 'Today · DROS',
        loadComponent: () => import('./features/today/today.component').then((m) => m.TodayComponent),
      },
      {
        path: 'company',
        title: 'Company · DROS',
        loadComponent: () =>
          import('./features/diamond-ridge/company/company.component').then((m) => m.CompanyComponent),
      },
      {
        path: 'company/settings',
        title: 'Company Settings · DROS',
        loadComponent: () =>
          import('./features/diamond-ridge/company/settings/company-settings.component').then(
            (m) => m.CompanySettingsComponent,
          ),
      },
      {
        path: 'projects',
        title: 'Projects · DROS',
        loadComponent: () =>
          import('./features/projects/management/projects-list.component').then(
            (m) => m.ProjectsListComponent,
          ),
      },
      {
        path: 'projects/:id',
        title: 'Project · DROS',
        loadComponent: () =>
          import('./features/projects/management/project-detail.component').then(
            (m) => m.ProjectDetailComponent,
          ),
      },
      {
        path: 'time',
        title: 'Time Tracking · DROS',
        loadComponent: () =>
          import('./features/me/time-tracking/time-tracking.component').then(
            (m) => m.TimeTrackingComponent,
          ),
      },
      {
        path: 'account',
        title: 'Account Settings · DROS',
        loadComponent: () =>
          import('./features/account/account-settings.component').then(
            (m) => m.AccountSettingsComponent,
          ),
      },
      {
        path: 'business-model',
        title: 'Business Model · DROS',
        // Authoring a checklist here can hold a lot of unsaved work — see
        // `unsavedChangesGuard`. Somebody built one, moved to another section
        // and lost it, which the amber "Unsaved changes" label did not prevent
        // because a label only works on somebody who is looking at it.
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('./features/diamond-ridge/business-model/business-model.component').then(
            (m) => m.BusinessModelComponent,
          ),
      },
      {
        path: 'org-chart',
        title: 'Org Chart · DROS',
        loadComponent: () =>
          import('./features/diamond-ridge/org-chart/org-chart.component').then((m) => m.OrgChartComponent),
      },
      {
        path: 'operating-systems',
        title: 'Operating Systems · DROS',
        loadComponent: () =>
          import('./features/diamond-ridge/operating-systems/operating-systems.component').then(
            (m) => m.OperatingSystemsComponent,
          ),
        // An imported operating system under review exists nowhere else, and the
        // read cannot be reopened from the page; the OS, phase and phase-detail
        // editors hold their changes until Save — see `unsavedChangesGuard`.
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'handbook',
        title: 'Handbook · DROS',
        loadComponent: () =>
          import('./features/diamond-ridge/handbook/handbook.component').then((m) => m.HandbookComponent),
      },
      {
        path: 'my-pathway',
        title: 'My Career Pathway · DROS',
        loadComponent: () =>
          import('./features/me/my-career-pathway/my-career-pathway.component').then(
            (m) => m.MyCareerPathwayComponent,
          ),
      },
      {
        path: 'users',
        title: 'Users & Access · DROS',
        loadComponent: () =>
          import('./features/admin/users-access/users-access.component').then(
            (m) => m.UsersAccessComponent,
          ),
      },
      {
        path: 'data',
        title: 'Data Management · DROS',
        loadComponent: () =>
          import('./features/admin/data-management/data-management.component').then(
            (m) => m.DataManagementComponent,
          ),
      },
      {
        path: 'awaiting-assignment',
        title: 'Awaiting Assignment · DROS',
        loadComponent: () =>
          import(
            './features/admin/outstanding-requirements/outstanding-requirements.component'
          ).then((m) => m.OutstandingRequirementsComponent),
      },
      {
        path: 'release-notes',
        title: 'Release Notes · DROS',
        loadComponent: () =>
          import('./features/admin/release-notes/release-notes.component').then(
            (m) => m.ReleaseNotesComponent,
          ),
      },
      {
        path: 'career-pathways',
        title: 'Career Pathways · DROS',
        loadComponent: () =>
          import('./features/diamond-ridge/career-pathways/career-pathways.component').then(
            (m) => m.CareerPathwaysComponent,
          ),
      },
      {
        path: 'people',
        title: 'People · DROS',
        loadComponent: () =>
          import('./features/diamond-ridge/people/people.component').then((m) => m.PeopleComponent),
      },
      {
        /*
         * ONE PERSON, AS A PAGE. It was a panel that unfolded inside a
         * directory row: the list scrolled away above it, nothing could be
         * linked to, and Back had nothing to go back to because nothing had
         * happened to the URL.
         *
         * A sibling route rather than a child, so it can be opened cold from a
         * link with no list behind it — it provides its own `PeopleStore`, and
         * the slice underneath is the same data either way.
         */
        path: 'people/:code',
        title: 'Person · DROS',
        // A departure being entered in the Record Departure modal asks before
        // the page is left (§8l).
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('./features/diamond-ridge/people/person-profile/person-profile.component').then(
            (m) => m.PersonProfileComponent,
          ),
      },
      {
        path: 'alerts',
        title: 'Alerts · DROS',
        loadComponent: () => import('./features/me/alerts/alerts.component').then((m) => m.AlertsComponent),
      },
      {
        path: 'my-training',
        title: 'My Training · DROS',
        loadComponent: () =>
          import('./features/me/my-training/my-training.component').then((m) => m.MyTrainingComponent),
      },
      {
        path: 'training',
        title: 'Training Modules · DROS',
        loadComponent: () =>
          import('./features/diamond-ridge/training/training.component').then((m) => m.TrainingComponent),
      },
      {
        path: 'team',
        title: 'My Team · DROS',
        loadComponent: () =>
          import('./features/me/my-team/my-team.component').then((m) => m.MyTeamComponent),
      },
      {
        path: 'get-started',
        title: 'Get Started · DROS',
        loadComponent: () =>
          import('./features/get-started/get-started.component').then((m) => m.GetStartedComponent),
      },
      {
        path: 'command',
        title: 'Command Center · DROS',
        loadComponent: () =>
          import('./features/command-center/command-center.component').then(
            (m) => m.CommandCenterComponent,
          ),
      },
      {
        path: 'checklists',
        title: 'Checklists · DROS',
        loadComponent: () =>
          import('./features/me/checklists/checklists.component').then((m) => m.ChecklistsComponent),
      },
      {
        path: 'onboarding-me',
        title: 'My Onboarding · DROS',
        loadComponent: () =>
          import('./features/me/my-onboarding/my-onboarding.component').then((m) => m.MyOnboardingComponent),
      },
      {
        path: 'rpm',
        title: 'RPM Plan · DROS',
        loadComponent: () => import('./features/me/rpm-plan/rpm-plan.component').then((m) => m.RpmPlanComponent),
      },
      {
        path: 'mrp',
        title: 'MRP · DROS',
        loadComponent: () => import('./features/me/mrp/mrp.component').then((m) => m.MrpComponent),
      },
      {
        path: 'my-projects',
        title: 'My Projects · DROS',
        loadComponent: () =>
          import('./features/me/my-projects/my-projects.component').then((m) => m.MyProjectsComponent),
      },
      {
        path: 'schedule',
        title: 'Weekly Schedule · DROS',
        loadComponent: () =>
          import('./features/me/weekly-schedule/weekly-schedule.component').then(
            (m) => m.WeeklyScheduleComponent,
          ),
      },
      {
        path: 'opportunities',
        title: 'Opportunities · DROS',
        loadComponent: () =>
          import('./features/projects/opportunities/opportunities.component').then(
            (m) => m.OpportunitiesComponent,
          ),
      },
      {
        path: 'proj-scheduling',
        title: 'Scheduling · DROS',
        loadComponent: () =>
          import('./features/projects/scheduling/scheduling.component').then((m) => m.SchedulingComponent),
      },
      {
        path: 'project-financials',
        title: 'Financials · DROS',
        loadComponent: () =>
          import('./features/projects/financials/financials.component').then((m) => m.FinancialsComponent),
      },
      {
        path: 'project-settings',
        title: 'Project Settings · DROS',
        loadComponent: () =>
          import('./features/projects/project-settings/project-settings.component').then(
            (m) => m.ProjectSettingsComponent,
          ),
      },
      {
        path: 'packet',
        title: 'My Packet · DROS',
        loadComponent: () =>
          import('./features/me/my-packet/my-packet.component').then((m) => m.MyPacketComponent),
      },
      {
        path: 'onboarding',
        title: 'Onboarding Process · DROS',
        loadComponent: () =>
          import('./features/diamond-ridge/onboarding-admin/onboarding-admin.component').then(
            (m) => m.OnboardingAdminComponent,
          ),
      },
      // Ported incrementally: scheduling, time tracking, org chart, etc.
    ],
  },
  { path: '**', redirectTo: '' },
];
